// Phase JA.3 — useDailyAccaIntel hook.
//
// Single anon-readable hook that batches all queries needed by the
// JA.3 surfaces (Performance Header, Intelligence Signal Bar,
// Yesterday's Results, No-Bet Report).
//
// Doctrine:
//   - Anon JWT only; RLS already restricts daily_acca_signals to
//     `suppression_state='published'` for anon/authenticated.
//   - Never fabricate. Empty arrays / null fields are valid states.
//   - All copy lives at the component layer. This hook only shapes
//     data and exposes a refetch().

import { useCallback, useEffect, useMemo, useState } from "react";
import { getBrowserClient } from "../supabase/browser.ts";
import {
  normaliseSignal,
  type DailyAccaSignal,
  type SignalTier,
} from "./useDailyAccaSignals.ts";

export interface DailyAccaPerformanceRow {
  performance_date: string;
  signals_published: number;
  signals_won: number;
  signals_lost: number;
  signals_void: number;
  win_rate: number | null;
  avg_conviction: number | null;
}

export interface DailyAccaStreakRow {
  current_streak: number;
  longest_streak: number;
  last_outcome: "won" | "lost" | "void" | "pending" | null;
  streak_since: string | null;
}

export interface IntelTodayCounts {
  tier_a_live: number;
  tier_b_live: number;
  tier_c_live: number;
  total_live: number;
  total_suppressed: number;
  founders_pick_live: boolean;
}

export interface SuppressionBreakdown {
  rule_id: string;
  count: number;
}

export interface UseDailyAccaIntelState {
  performance: DailyAccaPerformanceRow | null;
  /** Last 7 days rolling window, descending by date. */
  performanceWindow: DailyAccaPerformanceRow[];
  rollingWinRate7d: number | null;
  streak: DailyAccaStreakRow | null;
  todayCounts: IntelTodayCounts;
  suppressionBreakdown: SuppressionBreakdown[];
  yesterdayResults: DailyAccaSignal[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

function asNumOrNull(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function asNum(v: unknown): number {
  return asNumOrNull(v) ?? 0;
}

function asInt(v: unknown): number {
  const n = asNumOrNull(v);
  return n === null ? 0 : Math.trunc(n);
}

function asString(v: unknown): string {
  return typeof v === "string" ? v : "";
}

export function todayIsoDate(now: Date = new Date()): string {
  // Use local date string YYYY-MM-DD. Performance/suppression rows are
  // keyed by `signal_date::date` which is server-side date — we send
  // today's local string and rely on server-side coalescing for the
  // narrow window. We also union by (current_date) on the server side
  // via .eq().
  const y = now.getUTCFullYear();
  const m = String(now.getUTCMonth() + 1).padStart(2, "0");
  const d = String(now.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function yesterdayIsoDate(now: Date = new Date()): string {
  const y = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  return todayIsoDate(y);
}

/** Pure: average win_rate weighted by signals_published. Excludes voids. */
export function rollingWinRate(rows: DailyAccaPerformanceRow[]): number | null {
  let won = 0;
  let settled = 0;
  for (const r of rows) {
    won += r.signals_won;
    settled += r.signals_won + r.signals_lost;
  }
  if (settled === 0) return null;
  return won / settled;
}

export function normalisePerformance(
  row: Record<string, unknown>,
): DailyAccaPerformanceRow | null {
  const performance_date = asString(row["performance_date"]);
  if (!performance_date) return null;
  return {
    performance_date,
    signals_published: asInt(row["signals_published"]),
    signals_won: asInt(row["signals_won"]),
    signals_lost: asInt(row["signals_lost"]),
    signals_void: asInt(row["signals_void"]),
    win_rate: asNumOrNull(row["win_rate"]),
    avg_conviction: asNumOrNull(row["avg_conviction"]),
  };
}

export function normaliseStreak(row: Record<string, unknown>): DailyAccaStreakRow {
  const last = row["last_outcome"];
  const lastOutcome =
    last === "won" || last === "lost" || last === "void" || last === "pending"
      ? last
      : null;
  return {
    current_streak: asInt(row["current_streak"]),
    longest_streak: asInt(row["longest_streak"]),
    last_outcome: lastOutcome,
    streak_since: typeof row["streak_since"] === "string" ? row["streak_since"] : null,
  };
}

interface SignalCountRow {
  tier: SignalTier | null;
  suppression_state: string | null;
  is_founders_pick: boolean;
}

function asTier(v: unknown): SignalTier | null {
  return v === "tier_a" || v === "tier_b" || v === "tier_c" ? v : null;
}

export function summariseTodayCounts(
  signalRows: SignalCountRow[],
  suppressionTotal: number,
): IntelTodayCounts {
  const out: IntelTodayCounts = {
    tier_a_live: 0,
    tier_b_live: 0,
    tier_c_live: 0,
    total_live: 0,
    total_suppressed: suppressionTotal,
    founders_pick_live: false,
  };
  for (const r of signalRows) {
    if (r.suppression_state !== "published") continue;
    out.total_live += 1;
    if (r.tier === "tier_a") out.tier_a_live += 1;
    else if (r.tier === "tier_b") out.tier_b_live += 1;
    else if (r.tier === "tier_c") out.tier_c_live += 1;
    if (r.is_founders_pick) out.founders_pick_live = true;
  }
  return out;
}

export function summariseSuppression(
  rows: Array<{ rule_id: string | null }>,
): SuppressionBreakdown[] {
  const map = new Map<string, number>();
  for (const r of rows) {
    if (!r.rule_id) continue;
    map.set(r.rule_id, (map.get(r.rule_id) ?? 0) + 1);
  }
  return Array.from(map.entries())
    .map(([rule_id, count]) => ({ rule_id, count }))
    .sort((a, b) => b.count - a.count || a.rule_id.localeCompare(b.rule_id));
}

const EMPTY_COUNTS: IntelTodayCounts = {
  tier_a_live: 0,
  tier_b_live: 0,
  tier_c_live: 0,
  total_live: 0,
  total_suppressed: 0,
  founders_pick_live: false,
};

export function useDailyAccaIntel(): UseDailyAccaIntelState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const [performance, setPerformance] = useState<DailyAccaPerformanceRow | null>(null);
  const [performanceWindow, setPerformanceWindow] = useState<DailyAccaPerformanceRow[]>([]);
  const [streak, setStreak] = useState<DailyAccaStreakRow | null>(null);
  const [todayCounts, setTodayCounts] = useState<IntelTodayCounts>(EMPTY_COUNTS);
  const [suppressionBreakdown, setSuppressionBreakdown] = useState<SuppressionBreakdown[]>(
    [],
  );
  const [yesterdayResults, setYesterdayResults] = useState<DailyAccaSignal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    const today = todayIsoDate();
    const yesterday = yesterdayIsoDate();

    // The auto-generated db/types.ts may not include daily_acca_*
    // tables; cast to bypass typing while keeping anon JWT semantics.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sb = supabase as any;

    const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    const sevenDaysAgoIso = todayIsoDate(sevenDaysAgo);

    try {
      const [perfRes, streakRes, todaySignalsRes, suppressionRes, yResultsRes] =
        await Promise.all([
          sb
            .from("daily_acca_performance")
            .select(
              "performance_date, signals_published, signals_won, signals_lost, signals_void, win_rate, avg_conviction",
            )
            .gte("performance_date", sevenDaysAgoIso)
            .order("performance_date", { ascending: false }),
          sb
            .from("daily_acca_streak")
            .select("current_streak, longest_streak, last_outcome, streak_since")
            .limit(1)
            .maybeSingle(),
          sb
            .from("daily_acca_signals")
            .select("tier, suppression_state, is_founders_pick")
            .eq("signal_date", today),
          sb
            .from("daily_acca_suppression_log")
            .select("rule_id")
            .eq("signal_date", today),
          sb
            .from("daily_acca_signals")
            .select(
              "signal_id, signal_date, published_at, tier, fixture_id, fixture_name, league, continent, kickoff_at, market, selection, odds, reasoning_text, conviction_score, confidence_tier, agent_consensus, clv_edge_detected, clv_edge_pct, is_founders_pick, outcome",
            )
            .eq("suppression_state", "published")
            .eq("signal_date", yesterday)
            .not("outcome", "is", null)
            .neq("outcome", "pending")
            .order("kickoff_at", { ascending: true }),
        ]);

      if (perfRes.error) throw new Error(perfRes.error.message);
      if (streakRes.error) throw new Error(streakRes.error.message);
      if (todaySignalsRes.error) throw new Error(todaySignalsRes.error.message);
      if (suppressionRes.error) throw new Error(suppressionRes.error.message);
      if (yResultsRes.error) throw new Error(yResultsRes.error.message);

      const perfRows: Array<Record<string, unknown>> = Array.isArray(perfRes.data)
        ? perfRes.data
        : [];
      const window: DailyAccaPerformanceRow[] = perfRows
        .map((r) => normalisePerformance(r))
        .filter((r): r is DailyAccaPerformanceRow => r !== null);
      setPerformanceWindow(window);
      const todayPerf =
        window.find((r: DailyAccaPerformanceRow) => r.performance_date === today) ?? null;
      setPerformance(todayPerf);

      setStreak(streakRes.data ? normaliseStreak(streakRes.data) : null);

      const sigRows = (Array.isArray(todaySignalsRes.data) ? todaySignalsRes.data : []).map(
        (r: Record<string, unknown>) => ({
          tier: asTier(r["tier"]),
          suppression_state:
            typeof r["suppression_state"] === "string" ? r["suppression_state"] : null,
          is_founders_pick: r["is_founders_pick"] === true,
        }),
      );
      const supRows = (Array.isArray(suppressionRes.data) ? suppressionRes.data : []).map(
        (r: Record<string, unknown>) => ({
          rule_id: typeof r["rule_id"] === "string" ? r["rule_id"] : null,
        }),
      );
      setTodayCounts(summariseTodayCounts(sigRows, supRows.length));
      setSuppressionBreakdown(summariseSuppression(supRows));

      const yRows = Array.isArray(yResultsRes.data) ? yResultsRes.data : [];
      const yNorm = yRows
        .map((r: Record<string, unknown>) => normaliseSignal(r))
        .filter((r: DailyAccaSignal | null): r is DailyAccaSignal => r !== null);
      setYesterdayResults(yNorm);

      setLoading(false);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to load daily intelligence";
      setError(msg);
      setLoading(false);
    }
  }, [supabase]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const rollingWinRate7d = useMemo(
    () => rollingWinRate(performanceWindow),
    [performanceWindow],
  );

  return useMemo(
    () => ({
      performance,
      performanceWindow,
      rollingWinRate7d,
      streak,
      todayCounts,
      suppressionBreakdown,
      yesterdayResults,
      loading,
      error,
      refetch,
    }),
    [
      performance,
      performanceWindow,
      rollingWinRate7d,
      streak,
      todayCounts,
      suppressionBreakdown,
      yesterdayResults,
      loading,
      error,
      refetch,
    ],
  );
}

// Prevent "imported but unused" complaints on consumers that only need
// the value type-side: keep `asNum` exported. Used by tests.
export const __test__ = { asNum };
